import { ArrowRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const Hero = () => {
  const scrollToProjects = () => {
    const element = document.querySelector("#projects");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // The "Masked Reveal" Animation Variant
  const textReveal = {
    hidden: { y: "100%" },
    visible: { 
      y: "0%",
      transition: { duration: 1, ease: [0.76, 0, 0.24, 1] } // "Luxury" Easing
    }
  };

  const container = {
    visible: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center bg-white overflow-hidden pt-20">
      
      <div className="container-custom px-4 md:px-8 mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-[1fr_0.95fr] gap-8 items-center">
          
          {/* --- LEFT COLUMN: The Typography --- */}
          <div className="flex flex-col justify-center order-1 lg:order-1">
            
            {/* 1. Status Indicator */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex items-center gap-3 mb-8"
            >
              <div className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </div>
              <span className="text-xs font-bold tracking-widest uppercase text-slate-500">
                Available for new projects
              </span>
            </motion.div>

            {/* 2. Massive Masked Headline */}
            <motion.h1 
              className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-slate-900 leading-[0.95] tracking-tight mb-8 max-w-full break-words"
              variants={container}
              initial="hidden"
              animate="visible"
            >
              {/* Line 1 */}
              <div className="overflow-hidden">
                <motion.div variants={textReveal}>
                  Building Products
                </motion.div>
              </div>
              {/* Line 2 */}
              <div className="overflow-hidden flex items-center gap-4">
                <motion.div variants={textReveal} className="text-slate-900">
                  That
                </motion.div>
                {/* Italic/Blue Emphasis */}
                <motion.div variants={textReveal} className="italic text-blue-600 font-serif">
                  Matter.
                </motion.div>
              </div>
            </motion.h1>

            {/* 3. Description */}
            <motion.div
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: 0.6, duration: 0.8 }}
            >
              <p className="text-base sm:text-lg text-slate-500 max-w-lg leading-relaxed mb-10">
                I am <strong className="text-slate-900">Anant Mishra</strong>, a Product Manager bridging the gap between technical complexity and business strategy.
              </p>

              {/* 4. Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="rounded-full px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg font-bold bg-slate-900 hover:bg-blue-600 transition-colors duration-300"
                  onClick={scrollToProjects}
                >
                  View Selected Works
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                
                <a href="#contact" className="group flex items-center gap-2 px-8 py-4 border border-slate-200 rounded-full font-bold text-slate-900 hover:border-slate-900 transition-colors">
                  <Download className="h-5 w-5 text-slate-400 group-hover:text-slate-900 transition-colors" />
                  <span>Download Resume</span>
                </a>
              </div>
            </motion.div>
          </div>

          {/* --- RIGHT COLUMN: The Cinematic Portrait --- */}
          {/* Changed height from fixed (h-64...h-[700px]) to auto with specific aspect ratio */}
          <div className="relative order-2 lg:order-2 h-auto w-full mx-auto lg:mx-0">
             
             {/* The Container: aspect-[4/5] gives a nice 'magazine portrait' look without being too tall */}
             <div className="relative w-full aspect-square md:aspect-[5/5] rounded-[2rem] overflow-hidden group">
                
                {/* THE CURTAIN REVEAL (White block slides up) */}
                <motion.div
                  initial={{ height: "100%" }}
                  animate={{ height: "0%" }}
                  transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
                  className="absolute bottom-0 left-0 w-full bg-slate-100 z-20"
                />

                {/* The Image (Scale Out) */}
                {/* Added 'object-top' to ensure face is never cut off */}
                <motion.img
                  initial={{ scale: 1.4 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.4, ease: "easeOut" }}
                  src="/Anant.jpeg"
                  alt="Anant Mishra"
                  className="w-full h-full object-cover object-top transition-transform duration-700"
                />

                {/* Floating Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, duration: 0.5 }}
                  className="absolute bottom-6 right-6 bg-white/80 backdrop-blur-xl p-4 rounded-xl shadow-lg border border-white/50 z-30"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">PM</div>
                        <div>
                            <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Role</div>
                            <div className="font-bold text-slate-900">Product Manager</div>
                        </div>
                    </div>
                </motion.div>
             </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;